// The depth reader on the web (DEPTH.md; ios ReaderCanvasController.swift is the reference). A pinch scrubs a
// transition between the current level and the next: the page follows the fingers, the text under them stays under
// them, and past a threshold the new level commits. Browser input mapped to the pinch: trackpad pinch (ctrl+wheel on
// Chrome/Firefox/Edge, gesture events on Safari; no "release" there, so 160 ms without events counts as one), touch
// pinch (two-finger touch events), keyboard (- = [ ]) and the depth control.
import { account } from "../api/account";
import { ai, Priority } from "../levels/aiService";
import { documentFor, type DepthDocument } from "../levels/document";
import { levelsStore as levels } from "../levels/levelsStore";
import { unavailable } from "../levels/textClient";
import { anchorKeyFor, beatIndex, fits, Level, levelName, levelSource, LEVELS, sectionParagraphs, type AnchorKey } from "../levels/types";
import { chunks, type Article } from "../model/article";
import type { ArticleImage } from "../model/images";
import { library } from "../model/library";
import { textModel } from "../models";
import { chunkArticle } from "../narration/chunker";
import { player } from "../narration/player";
import { set, settings } from "../settings";
import { sync } from "../sync/sync";
import { h, icon } from "../ui/dom";
import { keyId, LevelView, type LevelContext, type NoteDisplay } from "./levelView";
import { smoothstep, Spring } from "./spring";

// DEPTH.md §4 numbers.
const OUT_T = 0.62, IN_T = 1.6, DEAD = 0.03, FOLLOW = 0.3;
const WHEEL_IDLE_MS = 160;

class Transition {
  p = 0;
  target: LevelView | null = null;
  sourceAnchor: HTMLElement | null = null;
  targetAnchor: HTMLElement | null = null;
  targetKey: AnchorKey | null = null;
  paragraph: number | null = null;
  constructor(readonly dir: number, readonly from: Level, readonly to: Level | null, readonly focal: { x: number; y: number }, readonly source: LevelView) {}
  get valid() { return this.to !== null; }
}

interface PinchState { base: number; lastP: number; lastTime: number; velocity: number; chained: boolean }

export interface ReaderHost {
  close(): void;
  openSettings(): void;
  openAsk(article: Article, paragraph: number): void;
}

export class Reader {
  readonly root: HTMLDivElement;
  private canvas: HTMLDivElement;
  private topBar: HTMLDivElement;
  private bottomBar: HTMLDivElement;
  private depthCtl: HTMLDivElement;
  private depthRing: HTMLDivElement;
  private depthName: HTMLDivElement;
  private depthSource: HTMLDivElement;
  private playBtn: HTMLButtonElement;
  private playStatus: HTMLDivElement;
  private rateBtn: HTMLButtonElement;
  private scrubber: HTMLDivElement;
  private scrubThumb: HTMLDivElement;
  private scrubBubble: HTMLDivElement;
  private peekEl: HTMLDivElement | null = null;
  private peekBeat: number | null = null;
  private coach: HTMLDivElement | null = null;

  private doc: DepthDocument;
  private key: string;
  level: Level = Level.Original;
  chapter = 0;
  readingParagraph = 0;
  readingOffset = 0;
  private shownLevel: Level = Level.Original;
  private depth = 0;
  private chromeHidden = false;

  current: LevelView | null = null;
  private transition: Transition | null = null;
  private pinch: PinchState | null = null;
  private spring: Spring | null = null;
  private busy = false;
  private needsRefresh = false;
  private spots = new Map<Level, { chapter: number; key: AnchorKey; top: number }>();
  private pendingReveal: number | null = null;
  private lastUserScroll = 0;
  private lastScrollTop = 0;
  private lastPositionSave = 0;
  private prefetchTimer: ReturnType<typeof setTimeout> | undefined;
  private scrubHide: ReturnType<typeof setTimeout> | undefined;
  private wheelTimer: ReturnType<typeof setTimeout> | undefined;
  private wheelScale = 1;
  private narrationTimer = 0;
  private unsubs: (() => void)[] = [];
  private markers: { fraction: number; label: string; summary: string; meta: string }[] = [];
  private scrubbing = false;
  private lastMarker = -1;

  constructor(private article: Article, private host: ReaderHost) {
    this.doc = documentFor(article)!;
    this.key = this.doc.key;
    this.canvas = h("div.canvas");
    this.scrubThumb = h("div.scrub-thumb");
    this.scrubBubble = h("div.scrub-bubble");
    this.scrubber = h("div.scrubber", { "aria-hidden": "true" }, h("div.scrub-track"), this.scrubThumb, this.scrubBubble);
    this.topBar = h("div.reader-top", null,
      h("button.icon-btn", { title: "Back to library (Esc)", "aria-label": "Back", onclick: () => this.host.close() }, icon("back")),
      h("div.reader-title", null, this.doc.title),
      h("button.icon-btn", { title: "Ask about this (A)", "aria-label": "Ask about this", onclick: () => this.openAsk(), dataset: { test: "ask" } }, icon("ask")),
      h("button.icon-btn", { title: "Text settings", "aria-label": "Text settings", onclick: (e: Event) => this.textMenu(e.currentTarget as HTMLElement) }, icon("text")));
    this.depthRing = h("div.depth-ring");
    this.depthName = h("div.depth-name");
    this.depthSource = h("div.depth-source");
    const stops = h("div.depth-stops", null, ...LEVELS.map((l) =>
      h("button", { class: l === Level.Original ? "stop square" : "stop", title: levelName(l), "aria-label": levelName(l), dataset: { level: String(l) },
        onclick: (e: Event) => { e.stopPropagation(); this.zoomTo(l); } })), this.depthRing);
    this.depthCtl = h("div.depth-control", { role: "group", "aria-label": "Reading depth" }, stops, h("div.depth-text", null, this.depthName, this.depthSource));
    this.playBtn = h("button.icon-btn.play", { "aria-label": "Play", title: "Play / pause (Space)", onclick: () => this.playPressed(), dataset: { test: "play" } }, icon("play"));
    this.rateBtn = h("button.rate", { title: "Speed", onclick: () => this.cycleRate() }, `${settings.playbackRate}×`);
    this.playStatus = h("div.play-status");
    const capsule = h("div.player-capsule", null,
      h("button.icon-btn", { "aria-label": "Back 15 seconds", title: "Back 15 s", onclick: () => player.skip(-15) }, icon("back15")),
      this.playBtn,
      h("button.icon-btn", { "aria-label": "Forward 30 seconds", title: "Forward 30 s", onclick: () => player.skip(30) }, icon("fwd30")),
      this.rateBtn);
    this.bottomBar = h("div.reader-bottom", null, this.playStatus, h("div.bottom-row", null, this.depthCtl, capsule));
    this.root = h("div.reader", null, this.canvas, this.scrubber, h("div.fade.top"), h("div.fade.bottom"), this.topBar, this.bottomBar);
  }

  // ---- lifecycle

  async mount(parent: HTMLElement) {
    parent.appendChild(this.root);
    await levels.load(this.key);
    sync.levelsReady(this.key); // DEPTH.md §8: this item's rows, before anything is generated for it
    library.markOpened(this.article.id);
    // DEPTH.md §6 "Opening the reader": narration of this item playing now; else the newest stored position.
    const stored = levels.position(this.key);
    this.level = stored ? (Math.max(-2, Math.min(1, stored.level)) as Level) : Level.Original;
    let p = stored?.paragraph ?? 0, off = stored?.offset ?? 0;
    const spot = player.articleId === this.article.id && player.isActive ? player.spot(this.article) : null;
    if (spot) { p = spot.paragraph; off = spot.word?.[0] ?? 0; }
    else {
      const listened = await player.storedSpot(this.article);
      if (listened && listened.at > (stored?.updatedAt ?? 0)) { p = listened.paragraph; off = listened.offset; }
    }
    this.shownLevel = this.level;
    this.depth = this.level;
    this.showChapter(0, p, off);
    this.updateDepthControl();
    this.updatePlayer();
    this.bindInput();
    this.unsubs.push(
      levels.events.on("changed", (e) => { if (e.key === this.key) this.scheduleRefresh(); }),
      ai.events.on("changed", (e) => { if (e.key === this.key) this.scheduleRefresh(); }),
      player.events.on("changed", () => this.updatePlayer()),
      library.events.on("changed", (id) => { if (id === this.article.id) this.articleChanged(); }),
      account.events.on("changed", () => { ai.accessChanged(); this.scheduleRefresh(); }),
    );
    this.narrationTimer = window.setInterval(() => this.tickNarration(), 100);
    document.addEventListener("keydown", this.onPageKey);
    this.unsubs.push(() => document.removeEventListener("keydown", this.onPageKey));
    if (!settings.hasSeenCoach) this.showCoach();
    this.root.focus?.();
  }

  destroy() {
    this.savePosition();
    clearInterval(this.narrationTimer);
    clearTimeout(this.prefetchTimer);
    for (const u of this.unsubs) u();
    this.spring?.stop();
    this.current?.clearNarration();
    ai.cancel(this.key, false);
    this.root.remove();
  }

  private articleChanged() {
    const a = library.get(this.article.id);
    if (!a) { this.host.close(); return; }
    this.article = a;
    const doc = documentFor(a);
    if (doc && doc !== this.doc) { this.doc = doc; this.scheduleRefresh(); }
  }

  // ---- page turns (DEPTH.md §9): page-turner and keyboard keys; browsers can't see the volume buttons

  private onPageKey = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(t.tagName))) return;
    if (this.root.querySelector(".lightbox, .dialog, .menu, .popover") || document.querySelector(".sheet-body, dialog[open]")) return;
    const forward = pageKeyDirection(e.key, e.shiftKey);
    if (forward === null) return;
    e.preventDefault();
    this.turnPage(forward);
  };

  /** One screen of text, instantly, one line short of the text area so a line cut by the edge comes back whole. */
  turnPage(forward: boolean) {
    const lv = this.current;
    if (!lv || this.transition || this.spring || this.pinch) return;
    const i = this.insets;
    const readable = this.height - i.top - i.bottom;
    if (readable <= 1) return;
    const line = (parseFloat(getComputedStyle(lv.el).fontSize) || 18) * 1.6;
    const step = pageStep(readable, line);
    lv.el.scrollTo({ top: Math.max(0, Math.min(lv.maxScroll, lv.el.scrollTop + (forward ? step : -step))), behavior: "auto" });
  }

  // ---- geometry

  private get insets() {
    const top = this.chromeHidden ? 0 : this.topBar.offsetHeight;
    return { top: this.topBar.offsetHeight, bottom: this.bottomBar.offsetHeight, visibleTop: top };
  }
  private get height() { return this.canvas.clientHeight; }
  /** The line treated as "where the reader is": 22% of the way down the text area. */
  private get readingLineY() { const i = this.insets; return i.top + (this.height - i.top - i.bottom) * 0.22; }

  // ---- building levels

  private context(chapter: number, readingParagraph?: number): LevelContext {
    const count = this.doc.chapters[chapter].paragraphs.length;
    const stored = levels.structure(this.key, chapter);
    const structure = fits(stored, count) ? stored : null;
    let display: LevelContext["structureDisplay"] = { kind: "ready" };
    if (!structure) {
      const st = ai.state({ kind: "structure", key: this.key, chapter });
      const why = unavailable(textModel());
      if (st?.state === "failed") display = { kind: "failed", message: st.message, needsKey: st.noKey };
      else display = why ? { kind: "failed", message: why, needsKey: true } : { kind: "loading" };
    }
    return {
      doc: this.doc, chapter, structure, structureDisplay: display, readingParagraph: readingParagraph ?? this.readingParagraph,
      note: (beat) => this.noteDisplay(chapter, beat),
      onTapAnchor: (k) => { const deeper = this.current && this.current.level < 1 ? ((this.current.level + 1) as Level) : null; if (deeper !== null) this.zoomTo(deeper, k, 0); },
      onRetryNote: (beat) => ai.retry({ kind: "note", key: this.key, chapter: this.chapter, beat }),
      onRetryStructure: () => ai.retry({ kind: "structure", key: this.key, chapter: this.chapter }),
      onSignIn: () => this.host.openSettings(),
      onImage: (img) => this.showImage(img),
    };
  }

  private noteDisplay(chapter: number, beat: number): NoteDisplay {
    const text = levels.note(this.key, chapter, beat);
    if (text !== null) return { kind: "text", text };
    const partial = ai.partialNote(this.key, chapter, beat);
    if (partial) return { kind: "streaming", text: partial };
    const st = ai.state({ kind: "note", key: this.key, chapter, beat });
    if (st?.state === "failed") return st.noKey ? { kind: "failed", message: st.message, needsKey: true } : { kind: "failed", message: "Couldn’t write this note. " + st.message, needsKey: false };
    const why = unavailable(textModel());
    if (why) return { kind: "failed", message: why, needsKey: true };
    return { kind: "loading" };
  }

  private makeLevel(level: Level, chapter: number, readingParagraph?: number): LevelView {
    const lv = new LevelView(level, this.context(chapter, readingParagraph));
    lv.el.addEventListener("scroll", () => this.onScroll(lv), { passive: true });
    return lv;
  }

  private scrollToParagraph(lv: LevelView, p: number, offset = 0) {
    if (p <= 0 && offset <= 0) { lv.el.scrollTop = 0; return; }
    const key = anchorKeyFor(lv.structure, p, lv.level);
    const a = key ? lv.anchor(key) : undefined;
    if (!a) { lv.el.scrollTop = 0; return; }
    const top = lv.level >= 0 ? lv.lineTop(p, offset) : null;
    lv.el.scrollTop += (top ?? lv.frame(a).top) - this.readingLineY;
  }

  showChapter(index: number, paragraph: number, offset = 0) {
    const ch = this.doc.chapters[index];
    if (!ch || !ch.paragraphs.length || this.transition || this.spring) return;
    const old = this.current;
    this.spots.clear();
    this.hidePeek();
    this.chapter = index;
    this.readingParagraph = Math.min(paragraph, ch.paragraphs.length - 1);
    this.readingOffset = this.readingParagraph === paragraph ? offset : 0;
    const lv = this.makeLevel(this.level, index);
    this.canvas.appendChild(lv.el);
    old?.el.remove();
    this.current = lv;
    this.scrollToParagraph(lv, this.readingParagraph, this.readingOffset);
    this.lastScrollTop = lv.el.scrollTop;
    this.requestForChapter();
    this.savePosition();
    this.updateMarkers();
    this.showScrubber(1800);
    this.setChrome(false);
  }

  private requestForChapter() {
    if (unavailable(textModel())) { ai.requestStructure(this.key, this.chapter, Priority.Now); return; }
    ai.requestWholeDocument(this.key, this.chapter);
    this.schedulePrefetch(1200);
  }

  private refreshScheduled = false;
  private scheduleRefresh() {
    if (this.refreshScheduled) return;
    this.refreshScheduled = true;
    requestAnimationFrame(() => { this.refreshScheduled = false; this.refresh(); });
  }

  private refresh() {
    const lv = this.current;
    if (!lv) return;
    if (this.transition || this.spring || this.busy) { this.needsRefresh = true; return; }
    this.needsRefresh = false;
    lv.update(this.context(this.chapter));
    this.updateMarkers();
    if (this.peekEl && this.peekBeat !== null) this.renderPeek();
    if (lv.level === Level.Explained) this.schedulePrefetch(200);
  }

  private schedulePrefetch(ms: number) {
    clearTimeout(this.prefetchTimer);
    this.prefetchTimer = setTimeout(() => this.prefetchVisibleNotes(), ms);
  }

  /** While reading the original, quietly prepare notes for what's on screen so pinching in is instant. */
  private prefetchVisibleNotes() {
    const lv = this.current;
    if (!lv || unavailable(textModel())) return;
    const explained = lv.level === Level.Explained;
    if (!(explained || (lv.level === Level.Original && settings.prefetchNotes))) return;
    const beats = lv.visibleBeats();
    const s = lv.structure;
    if (beats.length && s && beats[beats.length - 1] + 1 < s.beats.length) beats.push(beats[beats.length - 1] + 1);
    for (const b of beats) ai.requestNote(this.key, this.chapter, b, explained ? Priority.Visible : Priority.Prefetch);
  }

  // ---- scrolling, chrome, reading position

  private onScroll(lv: LevelView) {
    if (lv !== this.current || this.transition) return;
    const y = lv.el.scrollTop, dy = y - this.lastScrollTop;
    if (!this.programmaticScroll) {
      this.lastUserScroll = Date.now();
      if (dy > 8 && y > 60) this.setChrome(true);
      else if (dy < -8 || y <= 10) this.setChrome(false);
      this.showScrubber(1400);
    }
    this.programmaticScroll = false;
    this.lastScrollTop = y;
    this.updateScrubber();
    this.updateReadingPosition();
    this.updatePeekForScroll();
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => { this.schedulePrefetch(600); this.lastPositionSave = 0; this.updateReadingPosition(); this.savePosition(); }, 180);
  }
  private scrollEndTimer: ReturnType<typeof setTimeout> | undefined;
  private programmaticScroll = false;

  private setChrome(hidden: boolean) {
    if (hidden === this.chromeHidden) return;
    this.chromeHidden = hidden;
    this.root.classList.toggle("chrome-hidden", hidden);
  }

  private updateReadingPosition() {
    const lv = this.current;
    if (!lv || lv.level === Level.Outline) return;
    if (player.articleId === this.article.id && player.isPlaying && Date.now() - this.lastUserScroll > 5000) return;
    let paragraph: number, offset = 0;
    const line = lv.lineStart(this.readingLineY);
    if (line) ({ paragraph, offset } = line);
    else {
      const a = lv.anchorAt(this.readingLineY);
      const range = a ? lv.paragraphsOf(a.key) : null;
      if (!range) return;
      paragraph = range[0];
    }
    if (paragraph === this.readingParagraph && offset === this.readingOffset) return;
    this.readingParagraph = paragraph;
    this.readingOffset = offset;
    if (Date.now() - this.lastPositionSave > 2000) { this.lastPositionSave = Date.now(); this.savePosition(); }
  }

  savePosition() {
    levels.setPosition({ chapter: this.chapter, paragraph: this.readingParagraph, offset: this.readingOffset, level: this.level }, this.key);
  }

  // ---- scrubber (DEPTH.md §7)

  private showScrubber(ms: number) {
    const lv = this.current;
    if (!lv || lv.maxScroll < this.height * 0.5) { this.scrubber.classList.remove("visible"); return; }
    this.scrubber.classList.add("visible");
    clearTimeout(this.scrubHide);
    if (!this.scrubbing) this.scrubHide = setTimeout(() => this.scrubber.classList.remove("visible"), ms);
  }

  private updateScrubber() {
    const lv = this.current;
    if (!lv) return;
    const f = lv.maxScroll > 0 ? lv.el.scrollTop / lv.maxScroll : 0;
    const track = this.scrubber.clientHeight - 44;
    this.scrubThumb.style.transform = `translateY(${f * track}px)`;
  }

  private updateMarkers() {
    const lv = this.current, s = lv?.structure;
    this.scrubber.querySelectorAll(".scrub-tick").forEach((t) => t.remove());
    this.markers = [];
    if (!lv || !s || lv.maxScroll <= 0) { this.updateScrubber(); return; }
    s.sections.forEach((sec, i) => {
      const [a, b] = sectionParagraphs(s, i);
      const key = anchorKeyFor(s, a, lv.level);
      const el = key ? lv.anchor(key) : undefined;
      if (!el) return;
      const offset = lv.frame(el).top + lv.el.scrollTop - this.readingLineY;
      const fraction = i === 0 ? 0 : Math.min(1, Math.max(0, offset / lv.maxScroll));
      this.markers.push({ fraction, label: sec.label, summary: sec.summary, meta: a === b ? `¶ ${a + 1}` : `¶ ${a + 1}–${b + 1}` });
    });
    const track = this.scrubber.clientHeight - 44;
    for (const m of this.markers) this.scrubber.appendChild(h("div.scrub-tick", { style: { top: `${22 + m.fraction * track}px` } }));
    this.updateScrubber();
  }

  private scrubTo(clientY: number) {
    const lv = this.current;
    if (!lv || this.transition || this.spring) return;
    const r = this.scrubber.getBoundingClientRect();
    const f = Math.min(1, Math.max(0, (clientY - r.top - 22) / (r.height - 44)));
    this.lastUserScroll = Date.now();
    lv.el.scrollTop = f * lv.maxScroll;
    let idx = -1;
    this.markers.forEach((m, i) => { if (m.fraction <= f + 1e-6) idx = i; });
    this.scrubBubble.textContent = "";
    if (idx >= 0) {
      const m = this.markers[idx];
      this.scrubBubble.append(h("div.bubble-label", null, `${idx + 1} · ${m.label.toUpperCase()}`), h("div.bubble-summary", null, m.summary), h("div.meta", null, m.meta));
      if (idx !== this.lastMarker) navigator.vibrate?.(5);
    } else {
      const count = this.doc.chapters[this.chapter].paragraphs.length;
      this.scrubBubble.append(h("div.meta", null, `¶ ${this.readingParagraph + 1} of ${count}`));
    }
    this.lastMarker = idx;
    this.scrubBubble.style.transform = `translateY(${Math.max(0, clientY - r.top - 30)}px)`;
  }

  // ---- input

  private bindInput() {
    const c = this.canvas;
    // Trackpad pinch: Chrome/Firefox/Edge send ctrl+wheel; no end event, so a short idle counts as the release.
    c.addEventListener("wheel", (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const r = c.getBoundingClientRect();
      const focal = { x: e.clientX - r.left, y: e.clientY - r.top };
      if (!this.pinch) { if (!this.pinchBegan()) return; this.wheelScale = 1; }
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      this.wheelScale *= Math.exp(-dy / 100);
      this.pinchMoved(this.wheelScale, focal);
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => this.pinchEnded(false), WHEEL_IDLE_MS);
    }, { passive: false });
    // Safari's trackpad gestures.
    const g = (name: string, fn: (e: any) => void) => c.addEventListener(name, (e) => { e.preventDefault(); fn(e); }, { passive: false } as any);
    g("gesturestart", () => { this.pinchBegan(); });
    g("gesturechange", (e) => { const r = c.getBoundingClientRect(); this.pinchMoved(e.scale, { x: (e.clientX ?? r.width / 2) - r.left, y: (e.clientY ?? r.height / 2) - r.top }); });
    g("gestureend", () => this.pinchEnded(false));
    // Touch pinch: two fingers never scroll; a pinch may start while a scroll is under way and takes over.
    let startDist = 0;
    const touchInfo = (t: TouchList) => {
      const r = c.getBoundingClientRect();
      const [a, b] = [t[0], t[1]];
      return { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), x: (a.clientX + b.clientX) / 2 - r.left, y: (a.clientY + b.clientY) / 2 - r.top };
    };
    c.addEventListener("touchstart", (e) => {
      if (e.touches.length === 2) { e.preventDefault(); if (this.pinchBegan()) startDist = touchInfo(e.touches).d; }
    }, { passive: false });
    c.addEventListener("touchmove", (e) => {
      if (e.touches.length === 2 && this.pinch && startDist > 0) { e.preventDefault(); const t = touchInfo(e.touches); this.pinchMoved(t.d / startDist, t); }
    }, { passive: false });
    const touchEnd = (e: TouchEvent) => { if (this.pinch && startDist > 0 && e.touches.length < 2) { startDist = 0; this.pinchEnded(e.type === "touchcancel"); } };
    c.addEventListener("touchend", touchEnd);
    c.addEventListener("touchcancel", touchEnd);

    // Taps: hidden chrome comes back first; a tap on the author's text narrates from there when narration is loaded.
    c.addEventListener("click", (e) => {
      if ((e.target as Element).closest("button, a, .note, .figure-wrap, .gist-row, .outline-item")) return;
      if (this.chromeHidden) { this.setChrome(false); return; }
      const r = c.getBoundingClientRect();
      const pos = this.current?.textPosition(e.clientX - r.left, e.clientY - r.top);
      this.handleTap(pos ?? null);
    });
    c.addEventListener("dblclick", (e) => {
      const lv = this.current;
      if (!lv || lv.level >= Level.Explained || (e.target as Element).closest("button, .gist-row, .outline-item")) return;
      window.getSelection()?.removeAllRanges();
      const r = c.getBoundingClientRect();
      const a = lv.anchorAt(e.clientY - r.top);
      this.zoomTo((lv.level + 1) as Level, a?.key, a?.frac ?? 0.5);
    });
    c.addEventListener("contextmenu", (e) => this.wordMenu(e));

    // Scrubber drag.
    this.scrubber.addEventListener("pointerdown", (e) => {
      this.scrubbing = true;
      this.scrubber.setPointerCapture(e.pointerId);
      this.scrubber.classList.add("visible", "dragging");
      this.scrubTo(e.clientY);
    });
    this.scrubber.addEventListener("pointermove", (e) => { if (this.scrubbing) this.scrubTo(e.clientY); });
    const endScrub = () => {
      if (!this.scrubbing) return;
      this.scrubbing = false;
      this.scrubber.classList.remove("dragging");
      this.lastMarker = -1;
      this.showScrubber(1400);
      this.updateReadingPosition(); this.savePosition();
    };
    this.scrubber.addEventListener("pointerup", endScrub);
    this.scrubber.addEventListener("pointercancel", endScrub);
    window.addEventListener("resize", this.onResize);
    this.unsubs.push(() => window.removeEventListener("resize", this.onResize));
  }

  private onResize = () => { if (!this.transition && !this.spring) this.current?.preservingPosition(() => {}); this.updateMarkers(); };

  /** Keyboard (called by the app while the reader is open). */
  onKey(e: KeyboardEvent): boolean {
    if (e.metaKey || e.altKey) return false;
    const k = e.key;
    if (k === "=" || k === "+" || k === "]") { this.zoomIn(); return true; }
    if (k === "-" || k === "_" || k === "[") { this.zoomOut(); return true; }
    if (e.ctrlKey) return false;
    if (k === " ") { this.playPressed(); return true; }
    if (k === "Escape") { if (this.peekEl) this.hidePeek(); else this.host.close(); return true; }
    if (k === "a" || k === "A") { this.openAsk(); return true; }
    if (k === "1" || k === "2" || k === "3" || k === "4") { this.zoomTo(LEVELS[Number(k) - 1]); return true; }
    return false;
  }

  zoomIn() { if (this.current && this.current.level < 1) this.zoomTo((this.current.level + 1) as Level); }
  zoomOut() { if (this.current && this.current.level > -2) this.zoomTo((this.current.level - 1) as Level); }

  private handleTap(pos: { paragraph: number; offset: number } | null) {
    const p = pos ? this.doc.chapters[this.chapter].paragraphs[pos.paragraph] : null;
    if (pos && p?.dimmed) { this.askReadSkipped(pos.paragraph, p.skipReason); return; }
    if (pos && player.articleId === this.article.id && player.isLoaded) {
      player.seek(this.article, pos.paragraph, pos.offset, this.doc.chapters[this.chapter].paragraphs.length);
      this.lastUserScroll = 0;
      return;
    }
    if (window.getSelection()?.toString()) return;
    this.setChrome(!this.chromeHidden);
  }

  // ---- pinch (DEPTH.md §4)

  private pinchBegan(): boolean {
    if (this.spring || this.busy || !this.current) return false;
    this.pinch = { base: 1, lastP: 0, lastTime: performance.now(), velocity: 0, chained: false };
    return true;
  }

  private pinchMoved(scale: number, focal: { x: number; y: number }) {
    const state = this.pinch, lv = this.current;
    if (!state || !lv) return;
    const l = Math.log(scale / state.base);
    const dead = state.chained ? DEAD * 4 : DEAD;
    if (!this.transition) {
      if (Math.abs(l) < dead) return;
      this.transition = this.begin(l < 0 ? -1 : 1, focal, lv.anchorAt(focal.y));
      state.lastP = 0; state.lastTime = performance.now(); state.velocity = 0;
    }
    const tr = this.transition;
    if ((l < 0 ? -1 : 1) !== tr.dir && Math.abs(l) > dead) { // fingers reversed past the start: switch direction
      this.cancel(tr);
      this.transition = null;
      this.pinchMoved(scale, focal);
      return;
    }
    const limit = Math.abs(Math.log(tr.dir < 0 ? OUT_T : IN_T));
    const p = (l < 0 ? -1 : 1) === tr.dir ? Math.max(0, (Math.abs(l) - dead) / (limit - dead)) : 0;
    const now = performance.now(), dt = (now - state.lastTime) / 1000;
    if (dt > 0) state.velocity = state.velocity * 0.6 + ((p - state.lastP) / dt) * 0.4;
    state.lastP = p; state.lastTime = now;
    tr.p = p;
    this.apply(tr);
    if (tr.valid && p >= 1) { // detent: keep pinching to go another level
      this.commit(tr);
      this.transition = null;
      state.base = scale;
      state.chained = true;
    }
  }

  private pinchEnded(cancelled: boolean) {
    const state = this.pinch;
    if (!state) return;
    this.pinch = null;
    clearTimeout(this.wheelTimer);
    if (this.pendingReveal !== null) {
      const p = this.pendingReveal;
      this.pendingReveal = null;
      setTimeout(() => { if (this.current?.level === Level.Explained) this.revealNote(p); }, 450);
    }
    const tr = this.transition;
    if (!tr) return;
    if (!tr.valid || cancelled) {
      this.animate(tr, 0, 0, 320, () => { this.cancel(tr); this.transition = null; });
      return;
    }
    const go = state.chained ? tr.p > 0.7 : tr.p > 0.45 || (state.velocity > 1.5 && tr.p > 0.1);
    const v = go ? Math.max(state.velocity, 0) : Math.min(state.velocity, 0);
    this.animate(tr, go ? 1 : 0, v, 220, () => { go ? this.commit(tr) : this.cancel(tr); this.transition = null; });
  }

  /** Programmatic zoom: depth control, taps, keys (stiffness 190, or 300 when stepping several levels). */
  zoomTo(target: Level, anchorKey?: AnchorKey, frac = 0.5) {
    const lv = this.current;
    if (!lv || this.busy || this.spring || this.pinch || this.transition || target === lv.level) return;
    this.busy = true;
    const steps = Math.abs(target - lv.level);
    let a: { key: AnchorKey; el: HTMLElement; frac: number } | null = anchorKey && lv.anchor(anchorKey)
      ? { key: anchorKey, el: lv.anchor(anchorKey)!, frac } : lv.anchorAt(this.readingLineY);
    const step = () => {
      const cur = this.current!;
      if (cur.level === target) {
        this.busy = false;
        if (this.needsRefresh) this.refresh();
        return;
      }
      const dir = target > cur.level ? 1 : -1;
      let fy = this.height * 0.4;
      if (a) {
        const el = cur.anchor(a.key) ?? a.el;
        const f = cur.frame(el);
        fy = Math.min(this.height - 60, Math.max(this.insets.top, f.top + f.height * a.frac));
        a = { ...a, el };
      }
      const tr = this.begin(dir, { x: this.canvas.clientWidth / 2, y: fy }, a);
      this.transition = tr;
      this.animate(tr, 1, 0, steps > 1 ? 300 : 190, () => {
        this.commit(tr);
        this.transition = null;
        const next = this.current!;
        a = tr.targetKey && tr.targetAnchor ? { key: tr.targetKey, el: tr.targetAnchor, frac: 0.5 } : next.anchorAt(fy);
        step();
      });
    };
    step();
  }

  private begin(dir: number, focal: { x: number; y: number }, anchor: { key: AnchorKey; el: HTMLElement; frac: number } | null): Transition {
    const src = this.current!;
    const toN = src.level + dir;
    const to = toN < -2 || toN > 1 ? null : (toN as Level);
    const tr = new Transition(dir, src.level, to, focal, src);
    if (to === null) return tr;
    if (anchor) {
      const range = src.paragraphsOf(anchor.key);
      if (range) {
        const count = range[1] - range[0] + 1;
        tr.paragraph = range[0] + Math.min(count - 1, Math.floor(anchor.frac * count));
        anchor.el.classList.add("focused");
        tr.sourceAnchor = anchor.el;
      }
    }
    if (tr.paragraph !== null && src.level !== Level.Outline) this.readingParagraph = tr.paragraph;
    if (dir < 0 && anchor) this.spots.set(src.level, { chapter: this.chapter, key: anchor.key, top: src.frame(anchor.el).top });
    // Pinching in toward notes: the note for this passage first.
    const s = src.structure;
    if (to === Level.Explained && tr.paragraph !== null && s) {
      const b = beatIndex(s, tr.paragraph);
      if (b !== null) {
        ai.requestNote(this.key, this.chapter, b, Priority.Now);
        for (const vb of src.visibleBeats()) if (vb !== b) ai.requestNote(this.key, this.chapter, vb, Priority.Visible);
      }
    }
    if (!s && !unavailable(textModel())) ai.requestStructure(this.key, this.chapter, Priority.Now);

    const tgt = this.makeLevel(to, this.chapter, tr.paragraph ?? this.readingParagraph);
    tgt.el.style.opacity = "0";
    tgt.el.style.pointerEvents = "none";
    this.canvas.appendChild(tgt.el);
    const spot = this.spots.get(to);
    const back = spot && spot.chapter === this.chapter ? tgt.paragraphsOf(spot.key) : null;
    const here = anchor ? src.paragraphsOf(anchor.key) : null;
    if (dir > 0 && spot && back && here && back[0] <= here[1] && here[0] <= back[1] && tgt.anchor(spot.key)) {
      // Zooming back into the passage we left: exactly where the reader was.
      const ta = tgt.anchor(spot.key)!;
      tgt.el.scrollTop += tgt.frame(ta).top - spot.top;
      tr.paragraph = back[0];
      this.readingParagraph = back[0];
      ta.classList.add("focused");
      tr.targetAnchor = ta; tr.targetKey = spot.key;
    } else if (tr.paragraph !== null) {
      const key = anchorKeyFor(tgt.structure, tr.paragraph, to);
      const ta = key ? tgt.anchor(key) : undefined;
      if (key && ta) {
        const f = tgt.frame(ta);
        let delta: number;
        if (dir > 0 && tr.from < 0 && tr.sourceAnchor) delta = f.top - src.frame(tr.sourceAnchor).top; // into a summary: its start
        else delta = (f.top + (anchor?.frac ?? 0.5) * f.height) - focal.y;
        tgt.el.scrollTop += delta;
        ta.classList.add("focused");
        tr.targetAnchor = ta; tr.targetKey = key;
      } else if (to >= 0) this.scrollToParagraph(tgt, this.readingParagraph, this.readingOffset);
    } else if (to >= 0) this.scrollToParagraph(tgt, this.readingParagraph, this.readingOffset);
    tr.target = tgt;
    this.apply(tr);
    return tr;
  }

  private reduceMotion() { return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false; }

  private scaleAbout(lv: LevelView, focal: { x: number; y: number }, k: number) {
    lv.el.style.transformOrigin = `${focal.x}px ${focal.y + lv.el.scrollTop}px`;
    lv.el.style.transform = k === 1 ? "" : `scale(${k})`;
  }

  private apply(tr: Transition) {
    const src = tr.source;
    if (!tr.target || tr.to === null) { // rubber band past either end
      const r = 1 - Math.exp(-tr.p * 1.4);
      this.scaleAbout(src, tr.focal, 1 + tr.dir * 0.035 * r);
      this.setDepth(tr.from + tr.dir * 0.2 * r, tr.from);
      return;
    }
    const reduce = this.reduceMotion();
    const limit = tr.dir < 0 ? OUT_T : IN_T;
    const k = reduce ? 1 : Math.pow(limit, FOLLOW * tr.p);
    const k1 = reduce ? 1 : Math.pow(limit, FOLLOW);
    this.scaleAbout(src, tr.focal, k);
    src.el.style.opacity = String(1 - smoothstep(0, 0.7, tr.p));
    this.scaleAbout(tr.target, tr.focal, k / k1);
    tr.target.el.style.opacity = String(smoothstep(0.2, 1, tr.p));
    this.setDepth(tr.from + tr.dir * Math.min(1, Math.max(0, tr.p)), tr.p > 0.5 ? tr.to : tr.from);
  }

  private commit(tr: Transition) {
    const tgt = tr.target;
    if (!tgt || tr.to === null) return;
    tr.source.clearNarration();
    tr.source.el.remove();
    tgt.el.style.transform = ""; tgt.el.style.opacity = ""; tgt.el.style.pointerEvents = "";
    this.current = tgt;
    this.lastScrollTop = tgt.el.scrollTop;
    this.hidePeek();
    this.level = tr.to;
    this.setDepth(tr.to, tr.to);
    const line = tgt.lineStart(this.readingLineY);
    if (line) { this.readingParagraph = line.paragraph; this.readingOffset = line.offset; }
    else if (tr.paragraph !== null && tr.to !== Level.Outline) { this.readingParagraph = tr.paragraph; this.readingOffset = 0; }
    this.savePosition();
    if (tr.targetAnchor) { tr.targetAnchor.classList.remove("focused"); this.flash(tr.targetAnchor); }
    this.updateMarkers();
    navigator.vibrate?.(8);
    this.dismissCoach();
    this.announce(`${levelName(tr.to)}. ${levelSource(tr.to)}.`);
    if (tr.to >= 0) this.schedulePrefetch(300);
    if (tr.to === Level.Explained && tr.paragraph !== null) {
      if (this.pinch) this.pendingReveal = tr.paragraph;
      else { const p = tr.paragraph; setTimeout(() => this.revealNote(p), 200); }
    }
    if (this.needsRefresh && !this.spring && !this.busy) requestAnimationFrame(() => this.refresh());
  }

  private cancel(tr: Transition) {
    tr.target?.el.remove();
    tr.source.el.style.transform = "";
    tr.source.el.style.opacity = "";
    tr.sourceAnchor?.classList.remove("focused");
    this.setDepth(tr.from, tr.from);
    if (this.needsRefresh && !this.busy) requestAnimationFrame(() => this.refresh());
  }

  private animate(tr: Transition, to: number, velocity: number, stiffness: number, done: () => void) {
    const s = new Spring(tr.p, to, velocity, stiffness, (p) => { tr.p = p; this.apply(tr); }, () => { this.spring = null; done(); });
    this.spring = s;
    s.start();
  }

  private flash(el: HTMLElement) {
    el.classList.remove("flash");
    void el.offsetWidth;
    el.classList.add("flash");
    if (el.offsetHeight > this.height / 2) el.classList.add("tall");
    setTimeout(() => el.classList.remove("flash", "tall"), 1100);
  }

  private announce(text: string) {
    let live = this.root.querySelector(".sr-live") as HTMLElement | null;
    if (!live) { live = h("div.sr-live", { "aria-live": "polite" }); this.root.appendChild(live); }
    live.textContent = text;
  }

  // ---- depth control

  private setDepth(depth: number, shown: Level) {
    this.depth = depth;
    this.shownLevel = shown;
    this.updateDepthControl();
  }

  private updateDepthControl() {
    const stops = [...this.depthCtl.querySelectorAll<HTMLElement>(".stop")];
    const first = stops[0], last = stops[stops.length - 1];
    if (first && last && first.offsetWidth) {
      const x0 = first.offsetLeft + first.offsetWidth / 2, x1 = last.offsetLeft + last.offsetWidth / 2;
      const f = (this.depth + 2) / 3;
      this.depthRing.style.transform = `translateX(${x0 + (x1 - x0) * f - 11}px)`;
    }
    stops.forEach((s) => s.classList.toggle("active", Number(s.dataset.level) === this.shownLevel));
    this.depthName.textContent = levelName(this.shownLevel).toUpperCase();
    this.depthSource.textContent = levelSource(this.shownLevel).toUpperCase();
    this.depthCtl.classList.toggle("ai", this.shownLevel !== Level.Original);
    this.root.dataset.level = String(this.shownLevel);
  }

  // ---- notes: glide to the note, or a card when it's far (DEPTH.md §5)

  private revealNote(p: number) {
    const lv = this.current;
    if (!lv || lv.level !== Level.Explained || this.transition || this.spring || this.pinch) return;
    const s = lv.structure;
    const beat = s ? beatIndex(s, p) : null;
    const note = beat !== null ? lv.noteEls.get(beat) : undefined;
    if (!note || beat === null) return;
    const f = lv.frame(note), i = this.insets;
    const bottom = this.height - i.bottom - 12;
    if (f.bottom <= bottom) return;
    const readable = this.height - i.top - i.bottom;
    const delta = Math.min(f.bottom - bottom, f.top - (i.top + readable * 0.33));
    if (delta <= 0) return;
    if (delta <= readable * 0.5) lv.el.scrollTo({ top: Math.min(lv.maxScroll, lv.el.scrollTop + delta), behavior: this.reduceMotion() ? "auto" : "smooth" });
    else this.showPeek(beat);
  }

  private showPeek(beat: number) {
    this.hidePeek();
    this.peekBeat = beat;
    this.peekEl = h("div.peek", { role: "dialog", "aria-label": "Note" });
    this.root.appendChild(this.peekEl);
    this.renderPeek();
  }

  private renderPeek() {
    const el = this.peekEl, beat = this.peekBeat, s = this.current?.structure;
    if (!el || beat === null || !s?.beats[beat]) return;
    const b = s.beats[beat];
    const noteBox = h("aside.note");
    this.current!.renderNote(noteBox, beat);
    el.textContent = "";
    el.append(h("div.peek-head", null, h("span.meta", null, b.from === b.to ? `FOR ¶ ${b.from + 1}` : `FOR ¶ ${b.from + 1}–${b.to + 1}`),
      h("button.link", { onclick: () => { const lv = this.current, n = lv?.noteEls.get(beat); this.hidePeek(); if (lv && n) lv.el.scrollTo({ top: lv.el.scrollTop + lv.frame(n).top - this.insets.top - 24, behavior: "smooth" }); } }, "Show in text"),
      h("button.icon-btn.small", { "aria-label": "Close", onclick: () => this.hidePeek() }, icon("close", 18))), noteBox);
  }

  private hidePeek() { this.peekEl?.remove(); this.peekEl = null; this.peekBeat = null; }

  private updatePeekForScroll() {
    const lv = this.current;
    if (!this.peekEl || this.peekBeat === null || !lv) return;
    const n = lv.noteEls.get(this.peekBeat);
    if (!n) return;
    const f = lv.frame(n), card = this.peekEl.getBoundingClientRect(), base = this.canvas.getBoundingClientRect();
    if (f.top < card.top - base.top - 20 && f.bottom > this.insets.top) this.hidePeek();
  }

  // ---- narration (DEPTH.md §6)

  private tickNarration() {
    const lv = this.current;
    if (!lv || this.transition || this.spring || this.pinch || this.busy) return;
    const spot = player.articleId === this.article.id ? player.spot(this.article) : null;
    if (!spot) { lv.showNarration(null); return; }
    lv.showNarration(spot);
    const following = player.isPlaying && Date.now() - this.lastUserScroll > 5000;
    if (following && lv.level !== Level.Outline) {
      const off = spot.word?.[0] ?? 0;
      if (spot.paragraph !== this.readingParagraph || off !== this.readingOffset) {
        this.readingParagraph = spot.paragraph; this.readingOffset = off;
        if (Date.now() - this.lastPositionSave > 2000) { this.lastPositionSave = Date.now(); this.savePosition(); }
      }
    }
    if (!following) return;
    let rect: DOMRect | null = null;
    if (lv.level >= 0) rect = spot.word ? lv.rangeRect(spot.paragraph, spot.word[0], spot.word[1]) : (lv.paragraphEls.get(spot.paragraph) ? lv.frame(lv.paragraphEls.get(spot.paragraph)!) : null);
    else { const k = anchorKeyFor(lv.structure, spot.paragraph, lv.level); const el = k ? lv.anchor(k) : undefined; rect = el ? lv.frame(el) : null; }
    if (!rect) return;
    const i = this.insets, top = i.top + 16, bottom = this.height - i.bottom - 16;
    if (rect.top >= top && rect.bottom <= bottom) return;
    this.programmaticScroll = true;
    lv.el.scrollTo({ top: lv.el.scrollTop + rect.top - (i.top + (bottom - i.top) * 0.3), behavior: this.reduceMotion() ? "auto" : "smooth" });
  }

  /** Play continues from where you're reading (DEPTH.md §6). */
  private async playPressed() {
    const a = library.get(this.article.id) ?? this.article;
    if (!chunks(a).length) return;
    if (player.articleId === a.id && player.isActive) { player.pause(); return; }
    if (player.articleId === a.id && player.isLoaded && player.state.kind !== "failed") {
      const spot = player.spot(a);
      const near = spot && spot.paragraph === this.readingParagraph && Math.abs((spot.word?.[0] ?? 0) - this.readingOffset) <= 150;
      if (near || this.level === Level.Outline) { player.resume(); return; }
    }
    this.lastUserScroll = 0;
    await player.seek(a, this.readingParagraph, this.readingOffset, this.doc.chapters[this.chapter].paragraphs.length);
  }

  private updatePlayer() {
    const mine = player.articleId === this.article.id;
    const st = mine ? player.state : { kind: "idle" as const };
    const active = mine && player.isActive;
    this.playBtn.replaceChildren(icon(active ? "pause" : "play", 26));
    this.playBtn.setAttribute("aria-label", active ? "Pause" : "Play");
    this.playBtn.classList.toggle("busy", st.kind === "loading" || st.kind === "waiting");
    let text = "";
    if (st.kind === "loading") text = `Narrating part ${st.part + 1}…`;
    else if (st.kind === "waiting") text = st.reason;
    else if (st.kind === "failed") text = st.message;
    this.playStatus.textContent = text;
    if (st.kind === "failed") this.playStatus.append(" ", h("button.link", { onclick: () => player.resume() }, "Retry"));
    this.playStatus.classList.toggle("visible", !!text);
    this.rateBtn.textContent = `${settings.playbackRate}×`;
  }

  private cycleRate() {
    const rates = [0.8, 1, 1.15, 1.3, 1.5, 1.75, 2];
    const i = rates.indexOf(settings.playbackRate);
    set("playbackRate", rates[(i + 1) % rates.length]);
    player.setRate(settings.playbackRate);
    this.updatePlayer();
  }

  // ---- menus and dialogs

  private wordMenu(e: MouseEvent) {
    const lv = this.current;
    if (!lv || lv.level < 0) return;
    const r = this.canvas.getBoundingClientRect();
    const pos = lv.textPosition(e.clientX - r.left, e.clientY - r.top);
    if (!pos || !chunks(this.article).length) return;
    const text = this.doc.chapters[this.chapter].paragraphs[pos.paragraph]?.text ?? "";
    let a = pos.offset, b = pos.offset;
    while (a > 0 && !/\s/.test(text[a - 1])) a--;
    while (b < text.length && !/\s/.test(text[b])) b++;
    if (a === b) return;
    e.preventDefault();
    lv.markWord(pos.paragraph, [a, b]);
    const menu = h("div.menu", { style: { left: `${e.clientX - r.left}px`, top: `${e.clientY - r.top}px` } },
      h("button", { onclick: () => { close(); this.lastUserScroll = 0; player.seek(this.article, pos.paragraph, a, this.doc.chapters[this.chapter].paragraphs.length); } }, "Listen from here"));
    const close = () => { menu.remove(); lv.markWord(null); document.removeEventListener("pointerdown", outside, true); };
    const outside = (ev: Event) => { if (!menu.contains(ev.target as Node)) close(); };
    this.root.appendChild(menu);
    setTimeout(() => document.addEventListener("pointerdown", outside, true));
  }

  private askReadSkipped(paragraph: number, reason?: string) {
    const dlg = h("div.sheet-backdrop", { onclick: (e: Event) => { if (e.target === dlg) dlg.remove(); } },
      h("div.dialog", null, h("p", null, `Skipped: ${reason ?? "not narrated"}`),
        h("div.dialog-actions", null,
          h("button.btn", { onclick: () => dlg.remove() }, "Cancel"),
          h("button.btn.primary", { onclick: () => { dlg.remove(); this.readSkippedToo(paragraph); } }, "Read this too"))));
    this.root.appendChild(dlg);
  }

  /** Puts a skipped paragraph back into the narration (parts are chunked again; audio of unchanged parts is kept). */
  private readSkippedToo(paragraph: number) {
    library.editMeta(this.article.id, (m) => {
      const skipped = m.skipped ?? [], reasons = m.skipReasons ?? [];
      const i = skipped.indexOf(paragraph);
      if (i < 0) return;
      m.skipped = skipped.filter((_, k) => k !== i);
      m.skipReasons = reasons.filter((_, k) => k !== i);
      const paras = (m.text ?? m.sharedText ?? "").split("\n\n").map((p) => p.trim()).filter(Boolean);
      m.chunks = chunkArticle(paras, m.skipped).map((text, index) => ({ index, text }));
    });
  }

  private showImage(img: ArticleImage) {
    const images = this.doc.chapters[this.chapter].images;
    let i = Math.max(0, images.findIndex((x) => x.src === img.src));
    const pic = h("img"), cap = h("div.lightbox-caption");
    const show = () => { pic.src = images[i].src; pic.alt = images[i].alt || images[i].caption || "Image"; cap.textContent = images[i].caption || images[i].alt; };
    const close = () => { box.remove(); document.removeEventListener("keydown", keys, true); };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") { i = (i + 1) % images.length; show(); }
      else if (e.key === "ArrowLeft") { i = (i - 1 + images.length) % images.length; show(); }
      else return;
      e.stopPropagation(); e.preventDefault();
    };
    const box = h("div.lightbox", { onclick: (e: Event) => { if (e.target !== pic) close(); } }, pic, cap);
    show();
    document.addEventListener("keydown", keys, true);
    this.root.appendChild(box);
  }

  private textMenu(anchor: HTMLElement) {
    this.root.querySelector(".popover")?.remove();
    const sizes = [0, 1, 2, 3, 4];
    const pop = h("div.popover", null,
      h("div.pop-row", null, h("span", null, "Text size"),
        h("div.segmented", null, ...sizes.map((s) => h("button", { class: s === settings.textSize ? "on" : "", style: { fontSize: `${12 + s * 2}px` },
          onclick: () => { set("textSize", s); this.applyTextSize(); pop.remove(); } }, "A")))),
      h("div.pop-row", null, h("span", null, "Appearance"),
        h("div.segmented", null, ...(["system", "light", "dark"] as const).map((ap) => h("button", { class: ap === settings.appearance ? "on" : "",
          onclick: () => { set("appearance", ap); pop.remove(); } }, ap[0].toUpperCase() + ap.slice(1))))),
      h("div.pop-row", null, h("button.link", { onclick: () => { pop.remove(); this.regenerate(); } }, "Regenerate summaries and notes")));
    const r = anchor.getBoundingClientRect(), base = this.root.getBoundingClientRect();
    pop.style.top = `${r.bottom - base.top + 6}px`;
    pop.style.right = `${base.right - r.right}px`;
    this.root.appendChild(pop);
    const outside = (e: Event) => { if (!pop.contains(e.target as Node) && e.target !== anchor) { pop.remove(); document.removeEventListener("pointerdown", outside, true); } };
    setTimeout(() => document.addEventListener("pointerdown", outside, true));
  }

  applyTextSize() {
    document.documentElement.dataset.textSize = String(settings.textSize);
    this.current?.preservingPosition(() => {});
    this.updateMarkers();
  }

  private regenerate() {
    ai.cancel(this.key);
    levels.clear(this.key);
    this.refresh();
    this.requestForChapter();
  }

  private openAsk() { this.host.openAsk(library.get(this.article.id) ?? this.article, this.readingParagraph); }

  // ---- first run

  private showCoach() {
    const isTouch = matchMedia("(pointer: coarse)").matches;
    this.coach = h("div.coach", null,
      h("div.coach-title", null, "Pinch to change depth"),
      h("div.coach-text", null, isTouch ? "Pinch together for the gist and outline, spread for notes. Or tap the dots below."
        : "Pinch on the trackpad (or ctrl + scroll), press − and = , or click the dots below: together for the gist and outline, apart for notes."),
      h("button.btn.small", { onclick: () => this.dismissCoach() }, "Got it"));
    this.root.appendChild(this.coach);
  }

  private dismissCoach() {
    if (!this.coach) return;
    this.coach.remove();
    this.coach = null;
    set("hasSeenCoach", true);
  }

  /** For tests and debugging (window.depthDebug). */
  debugState() {
    return { level: this.level, shown: this.shownLevel, depth: this.depth, paragraph: this.readingParagraph, offset: this.readingOffset,
      anchors: this.current?.anchors.map((a) => keyId(a.key)).length ?? 0 };
  }
}

/** Which keys turn pages (DEPTH.md §9): forward (true), back (false), or not a page key (null). */
export function pageKeyDirection(key: string, shift: boolean): boolean | null {
  switch (key) {
    case "PageDown": case "ArrowDown": case "ArrowRight": return true;
    case "PageUp": case "ArrowUp": case "ArrowLeft": return false;
    case " ": case "Spacebar": return !shift;
    default: return null;
  }
}

/** How far a page turn scrolls: the text area less one line (never less than half of it). */
export function pageStep(readable: number, line: number): number {
  return Math.max(readable * 0.5, readable - line);
}
